<template>
  <div
    id="custom-form-container"
    :class="[layoutType, 'form-' + formType]"
    :style="{
      flex: {
        gap: gap
      },
      grid: {
        gridTemplateColumns: (new Array(groupCount)).fill('').map(() => '1fr').join(' '),
        gridGap: gap
      }
    }[layoutType]"
  >
    <div
      class="form-group"
      v-for="(form, index) in forms.filter(form => form.display ? form.display(result) : true)"
      v-show="form.width || form.flex || form.grid"
      :key="index"
      :class="{
        error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error,
        disabled: (form.disabled || (() => false))(result),
        'width-limit': widthLimit
      }"
      :style="{
        flex: form.width ? { flexBasis: form.width } : { flex: form.flex || 1 },
        grid: form.grid ? { gridColumn: 'span ' + form.grid } : {}
      }[layoutType]"
    >
      <div class="input-group">
        <form v-if="display" :data-vv-scope="scope">
          <div v-if="form.type || form.title" class="flex-start" :class="{ 'row': 'row-block', 'col': 'col-block' }[formType]">
            <div class="label row-block" v-if="form.title">
              <div v-if="formType === 'row'" :style="{ flex: 1 }"></div>
              <span class="title">{{ form.title }}</span>
              <span class="required" v-if="(form.validate && form.validate.includes('required') && form.required !== false) || form.required">{{ (form.requiredMsg || (() => '*'))(form) }}</span>
              <span class="description">{{ form.description }}</span>
              <span class="invisible">{{ form.invisible }}</span>
              <borCor
                v-if="form.info"
                v-bind="form.info.option"
                :background="'white'"
                :border="'#cccccc'"
                :radius="'0px'"
                :shadow="'1px 2px 3px rgba(0, 0, 0, 0.16)'"
                :blockStyle="{ padding: '5px', 'font-size': '0.89rem' }"
                :cursorSize="'40px 35px'"
              >
                <div slot="trigger">
                  <span>�</span>
                </div>
                <div slot="content" v-html="form.info.content">
                </div>
              </borCor>
            </div>
            <div v-if="form.type && result[form.value] !== undefined" class="options" :class="[form.type]" :style="{ '--inputScale': (form.inputScale || '100%') }">
              <template v-if="form.type === 'input'">
                <div>
                  <input type="text" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :readonly="form.readonly" :autocomplete="form.autocomplete || 'on'" :tabindex="form.tabindex" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'password'">
                <div>
                  <input :type="showPasswords.includes(form.value) ? 'text' : 'password'" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :autocomplete="form.autocomplete || 'on'" :tabindex="form.tabindex" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                  <div v-if="form.show" class="show center-block">
                    <i class="fa" :class="[showPasswords.includes(form.value) ? 'fa-eye' : 'fa-eye-slash']" aria-hidden="true" @click="e => showPasswords = showPasswords.includes(form.value) ? showPasswords.filter(v => v !== form.value) : showPasswords.concat([form.value])"></i>
                  </div>
                </div>
              </template>
              <template v-else-if="form.type === 'textarea'">
                <div>
                  <textarea :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :autocomplete="form.autocomplete || 'on'" :tabindex="form.tabindex" :rows="form.row" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'select'">
                <div>
                  <select :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :style="result[form.value] ? {} : { color: '#A9A9A9' }" v-model="result[form.value]" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)">
                    <option v-if="form.placeholder" value="" disabled>{{ form.placeholder(form) }}</option>
                    <option v-for="(option, index) in form.options" :key="index" :value="option.value || ''" :disabled="option.disabled" :style="{ color: '#333333' }">{{ option.title }}</option>
                  </select>
                </div>
              </template>
              <template v-if="form.type === 'datalist'">
                <div>
                  <input type="text" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :list="form.value + '-list'" :autocomplete="form.autocomplete || 'off'" :tabindex="form.tabindex" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                  <datalist :id="form.value + '-list'">
                    <option v-for="(option, index) in form.lists" :key="index">{{ option }}</option>
                  </datalist>
                </div>
              </template>
              <template v-else-if="form.type === 'boolean'">
                <div>
                  <label :for="form.value" :class="{ diabled: (form.disabled || (() => false))(result) || form.inputDisabled }">
                    <input type="checkbox" :id="form.value" :name="form.value" :tabindex="form.tabindex" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" v-model="result[form.value]" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                  </label>
                </div>
              </template>
              <template v-else-if="form.type === 'single'">
                <div v-for="(option, index) in form.options" :key="index">
                  <label :for="form.value + '_' + option.value" :class="{ diabled: (form.disabled || (() => false))(result) || form.inputDisabled }">
                    <input type="radio" :id="form.value + '_' + option.value" :name="form.value" :value="option.value" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" v-model="result[form.value]" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                    <span>{{ option.title }}</span>
                  </label>
                </div>
              </template>
              <template v-else-if="form.type === 'multiple'">
                <div v-for="(option, index) in form.options" :key="index">
                  <label :for="form.value + '_' + option.value" :class="{ diabled: (form.disabled || (() => false))(result) || form.inputDisabled || form.range && ((form.range[0] === (result[form.value] || []).length && result[form.value].includes(option.value)) || (form.range[1] === (result[form.value] || []).length && !result[form.value].includes(option.value))) }">
                    <input type="checkbox" :id="form.value + '_' + option.value" :name="form.value" :value="option.value" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled || (form.range && ((form.range[0] === (result[form.value] || []).length && result[form.value].includes(option.value)) || (form.range[1] === (result[form.value] || []).length && !result[form.value].includes(option.value))))" v-model="result[form.value]" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                    <span>{{ option.title }}</span>
                  </label>
                </div>
              </template>
              <template v-else-if="form.type === 'number'">
                <div>
                  <input type="number" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :step="form.step" :min="form.range ? form.range[0] : null" :max="form.range ? form.range[1] : null" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'month'">
                <div>
                  <input type="month" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :min="form.range ? form.range[0] : null" :max="form.range ? form.range[1] : '9999-12'" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" :style="result[form.value] ? {} : { color: '#A9A9A9' }" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'date'">
                <div>
                  <input type="date" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :min="form.range ? form.range[0] : null" :max="form.range ? form.range[1] : '9999-12-31'" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" :style="result[form.value] ? {} : { color: '#A9A9A9' }" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'time'">
                <div>
                  <input type="time" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :step="form.step" :min="form.range ? form.range[0] : null" :max="form.range ? form.range[1] : null" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" :style="result[form.value] ? {} : { color: '#A9A9A9' }" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'datetime'">
                <div>
                  <input type="datetime-local" :id="form.value" :name="form.value" :class="{ error: alerts[form.value] || errors.has(scope + '.' + form.value) || form.error }" :tabindex="form.tabindex" :step="form.step" :min="form.range ? form.range[0] : null" :max="form.range ? form.range[1] : null" :disabled="(form.disabled || (() => false))(result) || form.inputDisabled" :placeholder="(form.placeholder || (() => ''))(form)" :style="result[form.value] ? {} : { color: '#A9A9A9' }" v-model="result[form.value]" @change="e => (form.onChange || (() => {}))(result[form.value])" v-validate="form.validate" @keypress.enter.prevent.stop="e => $emit('enter', form)" />
                </div>
              </template>
              <template v-else-if="form.type === 'file'">
                <div>
                  <input
                    type="file"
                    v-show="false"
                    :id="form.value"
                    :name="form.value"
                    :multiple="form.multiple"
                    :accept="form.accept"
                    :extension="form.extension"
                    :disabled="(form.disabled || (() => false))(result) || form.inputDisabled"
                    @change="e => fileTypeCheck(e, e => result[form.value] = (e.target.files.length ? Array.from(e.target.files) : []), () => alert('error', form.fileErrorMsg || 'File Type Wrong'))"
                    v-validate="form.validate"
                  />
                  <button type="button" :tabindex="form.tabindex">
                    <span>{{ form.text || 'select' }}</span>
                    <label :for="form.value" :class="{ diabled: (form.disabled || (() => false))(result) || form.inputDisabled }"></label>
                  </button>
                  <template v-if="!$scopedSlots[form.value]">
                    <div v-for="(file, index) in result[form.value] || []" :key="index">
                      <span>{{ file.name }}</span>
                    </div>
                  </template>
                </div>
              </template>
              <template v-else-if="form.type === 'text'">
                <component :is="form.link || form.func ? 'a' : 'div'" :href="form.link" class="link" :class="{ 'input-style': form.inputStyle }" @click="e => (form.func || (() => {}))()">
                  <span :style="(form.textStyle || (() => {}))(result[form.value])">{{ (form.textDisplay || (x => x))(result[form.value]) }}</span>
                </component>
              </template>
              <div class="validation" :class="(alerts[form.value] || { type: 'error' }).type">
                <span v-if="alerts[form.value]">{{ alerts[form.value].msg }}</span>
                <span v-else-if="errors.has(scope + '.' + form.value) || form.error">{{ (form.alert || (() => null))(form, errors.first(scope + '.' + form.value)) || (errors.first(scope + '.' + form.value) || form.error).replace(form.value, form.errorTitle || form.title) }}</span>
              </div>
            </div>
          </div>
          <div class="custom" :class="{ 'only-custom': !form.type && !form.title }">
            <slot v-if="$scopedSlots[form.value]" :name="form.value" :data="result" :result="result[form.value]" :output="output[form.value]" :form="form"></slot>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import borCor from './borCor.vue'
import { fileTypeCheck } from '../../constants/global/util'

export default {
  components: {
    borCor
  },
  props: {
    forms: {
      type: Array,
      required: true
    },
    init: {
      type: Object,
      default: () => ({})
    },
    alerts: {
      type: Object,
      default: () => ({})
    },
    scope: {
      type: String,
      default: () => 'form'
    },
    // style
    widthLimit: {
      type: Boolean,
      default: () => false
    },
    layoutType: {
      type: String,
      default: () => 'flex'
    },
    formType: {
      type: String,
      default: () => 'row'
    },
    groupCount: {
      type: Number,
      default: () => 4
    },
    gap: {
      type: String,
      default: () => '20px 16px'
    }
  },
  data () {
    return {
      result: {},
      output: {},
      showPasswords: [],
      display: true
    }
  },
  computed: {
    // global
    fileTypeCheck () { return fileTypeCheck }
  },
  watch: {
    'forms' () {
    },
    'init' () {
      this.initialResults()
    },
    'result': {
      handler: async function () {
        this.output = await this.afterTreatment(this.result)
        this.$emit('change', this.output, this.$validator, this.errors)
      },
      deep: true
    }
  },
  created () {
    this.initialResults()
  },
  methods: {
    async initialResults () {
      let input = await this.preTreatment(this.init)
      let initials = {
        input: '',
        password: '',
        textarea: '',
        boolean: false,
        select: '',
        datalist: '',
        single: '',
        multiple: [],
        number: '',
        month: '',
        date: '',
        time: '',
        datetime: '',
        file: [],
        text: ''
      }
      this.result = this.forms.reduce((s, c) => ({ ...s, ...c.value ? { [c.value]: input[c.value] !== null && input[c.value] !== undefined ? input[c.value] : initials[c.type] } : {} }), {})
    },
    async preTreatment (init) {
      let input = {}
      for (let form of this.forms) {
        form.value && (input[form.value] = await (form.inputFormat || (async x => x))(init[form.value], form))
      }
      return input
    },
    async afterTreatment (result) {
      let output = {}
      for (let form of this.forms) {
        form.value && (output[form.value] = await (form.outputFormat || (async x => x))(result[form.value], form))
      }
      return output
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="sass">
#custom-form-container
  width: 100%
  box-sizing: border-box
  &.flex
    display: flex
    flex-direction: row
    flex-wrap: wrap
    align-items: flex-start
    justify-content: space-between
    .form-group
      width: 0
      padding-bottom: 20px
  &.grid
    display: grid
    .form-group
      width: 100%
  &.form-row
    .form-group
      .label
        flex: 1
        margin-top: 0.5rem
        margin-right: 24px
      .options
        width: 360px
        & > div
          width: var(--inputScale)
  &.form-col
    .form-group
      .label
        margin-bottom: 7px
      .options
        flex: 1
        width: 100%
  .form-group
    position: relative
    &.disabled
      opacity: 0.5
    &.width-limit
      overflow: hidden
    .input-group
      padding: 0
      form
        width: 100%
    .label
      white-space: nowrap
      .title
        font-size: 1.45rem
        white-space: nowrap
      .required
        color: #FA3D3D
        margin-left: 0
      .description
        font-size: 1.15rem
      .invisible
        font-size: 1.15rem
        color: transparent
      .bor-cor
        display: inline-block
        margin-left: 5px
    .options
      position: relative
      input, textarea, select
        &:disabled
          cursor: not-allowed !important
      label
        &.diabled
          cursor: not-allowed !important
      &.input
        & > div
          input[type="text"]
            width: 100%
            box-sizing: border-box
      &.password
        & > div
          position: relative
          input[type="password"], input[type="text"]
            width: 100%
            box-sizing: border-box
          .show
            position: absolute
            top: 0
            right: 0.3rem
            height: 100%
            cursor: pointer
            user-select: none
      &.textarea
        & > div
          textarea
            width: 100%
      &.select
        & > div
          select
            width: 100%
            option
      &.datalist
        & > div
          input[type="text"]
            width: 100%
            box-sizing: border-box
      &.boolean
        & > div
          display: flex
          label
            display: flex
            width: 100%
            padding: 3px
            &:hover
              background: #cccccc
              cursor: pointer
            input[type="checkbox"]
              margin-right: 5px
              padding: 1px 2px
      &.single
        & > div
          display: flex
          label
            display: flex
            align-items: center
            width: 100%
            padding: 3px
            &:hover
              background: #cccccc
              cursor: pointer
            input[type="radio"]
              margin-right: 5px
              margin-top: 1px
              padding: 1px 2px
            span
              word-break: break-word
      &.multiple
        & > div
          display: flex
          label
            display: flex
            align-items: center
            width: 100%
            padding: 3px
            cursor: pointer
            &.disabled
              cursor: not-allowed
              span
                color: #999999
            &:hover
              background: #cccccc
            input[type="checkbox"]
              margin-right: 5px
              margin-top: 1px
              padding: 1px 2px
            span
              word-break: break-word
      &.number
        & > div
          display: flex
          input[type="number"]
            width: 100%
      &.date
        & > div
          display: flex
          input[type="date"]
            width: 100%
      &.time
        & > div
          display: flex
          input[type="time"]
            width: 100%
      &.month
        & > div
          display: flex
          input[type="month"]
            width: 100%
      &.datetime
        & > div
          display: flex
          input[type="datetime-local"]
            width: 100%
      &.file
        & > div
          button
            position: relative
            label
              position: absolute
              width: 100%
              height: 100%
              top: 0
              left: 0
              cursor: pointer
      &.text
        & > a
          cursor: pointer
        & > div, & > a
          &.input-style
            display: flex
            min-height: 40px
            span
              display: flex
              align-items: center
    .custom
      &.only-custom
    .validation
      width: auto !important
      position: absolute
      &.error
        position: absolute
        top: 47px
        left: 25px
        span
          color: #FA3D3D
      &.success
        position: absolute
        top: 47px
        left: 25px
        span
          color: #83C91E
</style>



// WEBPACK FOOTER //
// src/components/tool/customForm.vue